import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon, ShoppingCart, LifeBuoy, FileCheck2, UserPlus, Trash2,
  ChevronRight, ChevronDown, RefreshCw, CheckCircle2, Inbox, Camera,
  BellRing,
} from "lucide-react";
import api from "../../lib/api";

/*
 * PriorityAlertBanner — thin, persistent "Action required" strip.
 *
 * Lives above the page content on every staff page (mounted in Layout).
 * Default: one-line pill showing "Action required · N items" (or "All clear").
 * Click the strip to expand → shows bucket tiles (per-user alerts, portal
 * uploads, portal orders/tickets, account applications, access requests,
 * doc removal requests, tickets needing me).
 *
 * Wave 19 #83: when ANY portal_uploads OR my_alerts are present the banner
 * pulses rose-red regardless of collapsed state — the user demanded the
 * team see incoming activity immediately instead of relying on side
 * channels (WhatsApp groups etc).
 *
 * Collapsed state is remembered per-session in localStorage so the user
 * doesn't have to re-collapse on every route change.
 */

// Wave 19 #83 — poll every 15s while the tab is visible. Matches the
// "feels instant" psychology of WhatsApp without melting the API. Web
// Push handles the closed-app case via VAPID (already wired in _notify).
const POLL_MS = 15_000;
const LS_KEY = "hv:prio-banner:open";

const BUCKETS = [
  { key: "my_alerts",       label: "Alerts for me",         desc: "Messages, mentions, assignments",  icon: BellRing,     to: "/alerts",               testId: "prio-bucket-my-alerts",      color: "#F43F5E", urgent: true },
  { key: "portal_uploads",  label: "Client photos",         desc: "Portal uploads waiting for review", icon: Camera,       to: "/portal-photos",        testId: "prio-bucket-portal-uploads", color: "#F43F5E", urgent: true },
  { key: "orders",          label: "Portal orders",         desc: "Waiting for staff review",       icon: ShoppingCart, to: "/order-requests",       testId: "prio-bucket-orders",       color: "#F43F5E" },
  { key: "tickets",         label: "Portal tickets",        desc: "Unassigned — need triage",       icon: LifeBuoy,     to: "/tickets?filter=portal", testId: "prio-bucket-tickets",      color: "#F59E0B" },
  { key: "my_tickets",      label: "Tickets needing me",    desc: "Open & assigned to you",         icon: Inbox,        to: "/tickets",              testId: "prio-bucket-my-tickets",   color: "#22C55E" },
  { key: "account_apps",    label: "Account applications", desc: "Waiting for credit decision",    icon: FileCheck2,   to: "/account-applications", testId: "prio-bucket-account-apps", color: "#1FB6C1" },
  { key: "access_requests", label: "Portal access requests", desc: "Users asking to join",         icon: UserPlus,     to: "/settings",             testId: "prio-bucket-access",       color: "#A78BFA" },
  { key: "doc_removals",    label: "Doc removal requests", desc: "Clients asking to remove docs",  icon: Trash2,       to: "/settings",             testId: "prio-bucket-removals",     color: "#EC4899" },
];

export default function PriorityAlertBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });

  const toggleOpen = () => {
    setOpen((v) => {
      const n = !v;
      try { localStorage.setItem(LS_KEY, n ? "1" : "0"); } catch {}
      return n;
    });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/priority-queue");
      setData(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load priority queue.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Wave 19 #83 — when client photo uploads OR direct alerts land,
  // force-open the drawer ONCE per session per count so the team
  // can't miss them. The user can still collapse manually after
  // acknowledging.
  useEffect(() => {
    const totalUrgent = (data?.portal_uploads?.count || 0) + (data?.my_alerts?.count || 0);
    if (totalUrgent > 0) {
      try {
        const ack = sessionStorage.getItem("hv:prio-urgent-ack");
        if (ack !== String(totalUrgent)) {
          setOpen(true);
          sessionStorage.setItem("hv:prio-urgent-ack", String(totalUrgent));
        }
      } catch { /* sessionStorage disabled */ }
    }
  }, [data?.portal_uploads?.count, data?.my_alerts?.count]);

  if (loading || err) return null;
  const total = data?.total || 0;
  const allClear = total === 0;
  // Wave 19 #83 — both portal photo uploads AND any directed alert for
  // me must immediately catch the eye. Either bucket > 0 → banner
  // pulses rose-red and auto-expands once.
  const portalUploadsCount = data?.portal_uploads?.count || 0;
  const myAlertsCount = data?.my_alerts?.count || 0;
  const isUrgent = portalUploadsCount > 0 || myAlertsCount > 0;

  return (
    <div
      className={`rounded-md border overflow-hidden transition-all ${
        allClear
          ? "border-emerald-500/30 bg-emerald-500/[0.04]"
          : isUrgent
            ? "border-rose-500/40"
            : "border-rose-500/30"
      }`}
      data-testid={allClear ? "prio-banner-clear" : (isUrgent ? "prio-banner-urgent" : "prio-banner")}
    >
      {/* ── One-line strip ─────────────────────────────────────────────
          Wave 19 #91 — only this strip pulses, carries the rose gradient
          AND the rose glow shadow. Outer container stays calm so the
          drawer below doesn't bleed red onto the dashboard cards. */}
      <div
        className={`${
          allClear
            ? ""
            : isUrgent
              ? "animate-[pulse_2s_ease-in-out_infinite] shadow-[0_0_36px_-4px_rgba(244,63,94,0.55)]"
              : "shadow-[0_0_24px_-8px_rgba(244,63,94,0.35)]"
        }`}
        style={
          allClear
            ? undefined
            : isUrgent
              ? { background: "linear-gradient(90deg, rgba(244,63,94,0.28) 0%, rgba(244,63,94,0.14) 50%, rgba(244,63,94,0.22) 100%)" }
              : { background: "linear-gradient(90deg, rgba(244,63,94,0.14) 0%, rgba(244,63,94,0.05) 50%, rgba(245,158,11,0.06) 100%)" }
        }
      >
      <button
        type="button"
        onClick={toggleOpen}
        disabled={allClear}
        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${
          allClear ? "cursor-default" : "hover:bg-white/[0.03] cursor-pointer"
        }`}
        data-testid="prio-banner-toggle"
        aria-expanded={open}
      >
        {/* Leading icon / pulse */}
        {allClear ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        ) : (
          <span className="relative inline-flex w-3.5 h-3.5 shrink-0 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-rose-500/50 animate-ping" />
            <AlertOctagon className="relative w-3.5 h-3.5 text-rose-300" />
          </span>
        )}

        {/* Label + count */}
        <span className={`text-xs font-semibold uppercase tracking-wider ${
          allClear ? "text-emerald-200" : isUrgent ? "text-rose-50" : "text-rose-100"
        }`}>
          {allClear
            ? "All clear"
            : isUrgent
              ? (myAlertsCount > 0 && portalUploadsCount > 0
                  ? `${myAlertsCount} alert${myAlertsCount === 1 ? "" : "s"} · ${portalUploadsCount} client photo${portalUploadsCount === 1 ? "" : "s"}`
                  : myAlertsCount > 0
                    ? `${myAlertsCount} alert${myAlertsCount === 1 ? "" : "s"} for you`
                    : `Client photos uploaded · ${portalUploadsCount}`)
              : "Action required"}
        </span>
        {!allClear && (
          <span
            className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-rose-500/30 border border-rose-400/50 text-rose-50 shrink-0"
            data-testid="prio-banner-total"
          >
            {total}
          </span>
        )}

        {/* Inline bucket dots (teaser — only when collapsed) */}
        {!allClear && !open && (
          <span className="hidden sm:flex items-center gap-1 ml-1 flex-1 min-w-0 truncate">
            {BUCKETS.filter((b) => (data?.[b.key]?.count || 0) > 0).slice(0, 6).map((b) => (
              <span
                key={b.key}
                className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border"
                style={{ borderColor: `${b.color}55`, background: `${b.color}10`, color: b.color }}
              >
                <b.icon className="w-2.5 h-2.5" />
                {data[b.key].count}
              </span>
            ))}
          </span>
        )}
        {allClear && (
          <span className="text-[10px] text-[#a0aabf] hidden sm:inline">
            No portal items need your attention right now.
          </span>
        )}

        <span className="flex-1" />

        {/* Refresh */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); setRefreshing(true); load(); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setRefreshing(true); load(); } }}
          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-white/5 text-[#a0aabf] shrink-0"
          data-testid="prio-refresh"
          title="Re-check"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
        </span>

        {/* Expand chevron */}
        {!allClear && (
          open
            ? <ChevronDown className="w-3.5 h-3.5 text-rose-200 shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-rose-200 shrink-0" />
        )}
      </button>
      </div>

      {/* ── Expanded drawer — Iter 418.ab (Dirk, 2026-02-28) — only show
          buckets that actually have items so the drawer stays small. If
          just one bucket is hot, one horizontal strip; two hot → two
          strips; etc. Zero-count buckets are hidden entirely. ─── */}
      {open && !allClear && (() => {
        const hotBuckets = BUCKETS.filter((b) => (data?.[b.key]?.count || 0) > 0);
        if (hotBuckets.length === 0) return null;
        return (
          <div className="px-3 pb-3 pt-1 border-t border-rose-500/20 bg-[#0a0d14] flex flex-col gap-1.5">
            {hotBuckets.map((b) => {
              const count = data[b.key].count;
              const Icon = b.icon;
              return (
                <Link
                  key={b.key}
                  to={b.to}
                  data-testid={b.testId}
                  className="flex items-center gap-3 rounded-md border p-2 transition hover:brightness-110"
                  style={{
                    borderColor: `${b.color}88`,
                    background: `${b.color}14`,
                  }}
                >
                  <div
                    className="w-7 h-7 rounded-md border flex items-center justify-center shrink-0"
                    style={{ borderColor: `${b.color}55`, background: `${b.color}1f` }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: b.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-white truncate">{b.label}</div>
                    <div className="text-[10px] text-[#a0aabf] truncate">{b.desc}</div>
                  </div>
                  <span
                    className="font-display text-lg font-bold tabular-nums shrink-0"
                    style={{ color: b.color }}
                  >
                    {count}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#647087] shrink-0" />
                </Link>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
